/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useCallback, useState } from 'react';
import { AgenciesService } from '@services/requests';
import type { ChangeLine } from '../components/plan-flow/plan-flow.items';
import { refusalOf, type PreviewRefusal } from './previewRefusal';
import type { ChangeItemsPreview } from '@/services/requests/housien-services/AgenciesService.team';

export interface UseSeatAwarePlanChangeResult {
  preview: ChangeItemsPreview | null;
  previewFailed: boolean;
  /** The server said no and why; a retry cannot change that. */
  refusal: PreviewRefusal | null;
  isPending: boolean;
  isError: boolean;
  /** Asks what the lines would cost, charging nothing. */
  fetchPreview: (lines: ChangeLine[]) => Promise<void>;
  /** Commits the lines; false when the change was refused. */
  apply: (lines: ChangeLine[]) => Promise<boolean>;
  reset: () => void;
}

/**
 * Changing a team's plan or cycle through the lines it actually holds.
 *
 * `change-plan` describes a subscription as one plan at one price, which is
 * the solo broker's shape: pointed at an agency it names a bill the agency
 * does not have. `change-items` is the same question asked per plan, with
 * the seat counts carried over, and it is the one that can answer for a team.
 *
 * @param agencyId - The agency whose subscription is changing.
 * @returns The preview, the commit and their states.
 */
export function useSeatAwarePlanChange(agencyId: string): UseSeatAwarePlanChangeResult {
  const [preview, setPreview] = useState<ChangeItemsPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [refusal, setRefusal] = useState<PreviewRefusal | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchPreview = useCallback(async (lines: ChangeLine[]) => {
    setPreviewFailed(false);
    setRefusal(null);
    setPreview(null);
    try {
      setPreview(await AgenciesService.previewSubscriptionItems(agencyId, lines));
    } catch (error) {
      const reason = refusalOf(error);
      setRefusal(reason);
      setPreviewFailed(reason === null);
    }
  }, [agencyId]);

  const apply = useCallback(async (lines: ChangeLine[]) => {
    setIsPending(true);
    setIsError(false);
    try {
      await AgenciesService.changeSubscriptionItems(agencyId, lines);
      return true;
    } catch {
      setIsError(true);
      return false;
    } finally {
      setIsPending(false);
    }
  }, [agencyId]);

  const reset = useCallback(() => {
    setPreview(null);
    setPreviewFailed(false);
    setRefusal(null);
    setIsError(false);
  }, []);

  return { preview, previewFailed, refusal, isPending, isError, fetchPreview, apply, reset };
}

export default useSeatAwarePlanChange;
